import { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import DashboardTemplate from "@components/DashboardTemplate";
import CommonTemplate from "@components/CommonTemplate";
import ProtectedRouteWrapper from "@components/ProtectedRouteWrapper";
import LoadingContent from "@components/LoadingContent";

/** Redirects /:domain/edit/:id → /:domain/:id/edit preserving search params. */
function EditRedirect({ base }: { base: string }) {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`${base}/${id}/edit`} replace />;
}

/** Redirects /promoitems/:id/promoitems-plans/edit/:idEdit */
function PromoitemsPlanEditRedirect() {
  const { id, idEdit } = useParams<{ id: string; idEdit: string }>();
  return <Navigate to={`/promoitems/${id}/promoitems-plans/${idEdit}/edit`} replace />;
}

/** Plan vínculo: full-page routes → PromoitemView + query (sheet / dialog). */
function PromoitemsPlanHubNewRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/promoitems/${id}?plan=new`} replace />;
}

function PromoitemsPlanHubEditRedirect() {
  const { id, idEdit } = useParams<{ id: string; idEdit: string }>();
  return <Navigate to={`/promoitems/${id}?planEdit=${idEdit}`} replace />;
}

function PromoitemsPlanHubDetailRedirect() {
  const { id, idEdit } = useParams<{ id: string; idEdit: string }>();
  return <Navigate to={`/promoitems/${id}?planDetail=${idEdit}`} replace />;
}

/** Redirects /coupons/:id/coupon-plans/edit/:idEdit */
function CouponPlanEditRedirect() {
  const { id, idEdit } = useParams<{ id: string; idEdit: string }>();
  return <Navigate to={`/coupons/${id}/coupon-plans/${idEdit}/edit`} replace />;
}

/** Redirects /cms/sites/edit/:configId */
function CmsSiteEditRedirect() {
  const { configId } = useParams<{ configId: string }>();
  return <Navigate to={`/cms/sites/${configId}/edit`} replace />;
}

/** Redirects /cms/plans/edit/:planIndex */
function CmsPlanEditRedirect() {
  const { planIndex } = useParams<{ planIndex: string }>();
  return <Navigate to={`/cms/plans/${planIndex}/edit`} replace />;
}

/** Redirects /cms/leituverso/edit/:modalIndex */
function CmsLeituversoEditRedirect() {
  const { modalIndex } = useParams<{ modalIndex: string }>();
  return <Navigate to={`/cms/leituverso/${modalIndex}/edit`} replace />;
}

// Public
const Login = lazy(() => import("@/pages/public/Auth/Login"));
const OAuthCallback = lazy(() => import("@/pages/public/Auth/OAuthCallback"));
const HealthcheckPage = lazy(() => import("@/pages/public/Healthcheck"));

// Private — Dashboard
const Dashboard = lazy(() => import("@/pages/private/Dashboard/Index"));

// Plans
const PlansList = lazy(() => import("@/pages/private/Plans/List/PlanList"));
const PlansForm = lazy(() => import("@/pages/private/Plans/Form/PlanForm"));
const PlansView = lazy(() => import("@/pages/private/Plans/View/PlanView"));

// Coupons
const CouponsList = lazy(() => import("@/pages/private/Coupons/List/CouponList"));
const CouponsForm = lazy(() => import("@/pages/private/Coupons/Form/CouponForm"));
const CouponsView = lazy(() => import("@/pages/private/Coupons/View/CouponView"));
const CouponPlansList = lazy(() => import("@/pages/private/CouponPlans/List/CouponPlanList"));
const CouponPlansForm = lazy(() => import("@/pages/private/CouponPlans/Form/CouponPlanForm"));
const CouponPlansView = lazy(() => import("@/pages/private/CouponPlans/View/CouponPlanView"));

// Promoitems
const PromoItemsList = lazy(() => import("@/pages/private/Promoitems/List/PromoitemList"));
const PromoItemsForm = lazy(() => import("@/pages/private/Promoitems/Form/PromoitemForm"));
const PromoItemsView = lazy(() => import("@/pages/private/Promoitems/View/PromoitemView"));
const PromoitemProducts = lazy(() => import("@/pages/private/PromoitemProducts/List/PromoitemProductList"));
const PromoitemProductsForm = lazy(
  () => import("@/pages/private/PromoitemProducts/Form/PromoitemProductForm")
);
const PromoitemProductsView = lazy(
  () => import("@/pages/private/PromoitemProducts/View/PromoitemProductView")
);
const PromoitemsPlansList = lazy(() => import("@/pages/private/PromoitemsPlans/List/PromoitemsPlanList"));

// Leituverso
const Leituverso = lazy(() => import("@/pages/private/Leituverso/List/LeituversoList"));
const LeituversoForm = lazy(() => import("@/pages/private/Leituverso/Form/LeituversoForm"));
const LeituversoView = lazy(() => import("@/pages/private/Leituverso/View/LeituversoView"));

// Promotions
const PromotionsList = lazy(() => import("@/pages/private/Promotions/List/PromotionList"));
const PromotionsForm = lazy(() => import("@/pages/private/Promotions/Form/PromotionForm"));
const PromotionsView = lazy(() => import("@/pages/private/Promotions/View/PromotionView"));

// Ambassadors
const AmbassadorsList = lazy(() => import("@/pages/private/Ambassadors/List/AmbassadorList"));
const AmbassadorsForm = lazy(() => import("@/pages/private/Ambassadors/Form/AmbassadorForm"));
const AmbassadorsView = lazy(() => import("@/pages/private/Ambassadors/View/AmbassadorView"));

// Indications
const IndicationsForm = lazy(() => import("@/pages/private/Indications/Form/IndicationForm"));

// Prizes
const PrizesList = lazy(() => import("@/pages/private/Prizes/List/PrizeList"));
const PrizesForm = lazy(() => import("@/pages/private/Prizes/Form/PrizeForm"));
const PrizesView = lazy(() => import("@/pages/private/Prizes/View/PrizeView"));

// Levels
const LevelsList = lazy(() => import("@/pages/private/Levels/List/LevelList"));
const LevelsForm = lazy(() => import("@/pages/private/Levels/Form/LevelForm"));
const LevelsView = lazy(() => import("@/pages/private/Levels/View/LevelView"));

// CMS Manager
const CMSDashboard = lazy(
  () => import("@/features/cms-manager/pages/CMSDashboard")
);
const SitesManager = lazy(
  () => import("@/features/cms-manager/pages/SitesManager")
);
const SiteForm = lazy(() => import("@/features/cms-manager/pages/SiteForm"));
const PlansManager = lazy(
  () => import("@/features/cms-manager/pages/PlansManager")
);
const PlanForm = lazy(() => import("@/features/cms-manager/pages/PlanForm"));
const CmsLeituversosManager = lazy(
  () => import("@/features/cms-manager/pages/LeituversosPlanoMaisManager")
);
const CmsLeituversoForm = lazy(
  () => import("@/features/cms-manager/pages/LeituversoPlanoMaisForm")
);
const FlexGiftForm = lazy(
  () => import("@/features/cms-manager/pages/FlexGiftForm")
);

const fallback = <LoadingContent />;

export default function Router() {
  return (
    <Routes>
      {/* Public */}
      <Route
        path="/login"
        element={
          <Suspense fallback={fallback}>
            <CommonTemplate page={Login} />
          </Suspense>
        }
      />
      <Route
        path="/healthcheck"
        element={
          <Suspense fallback={fallback}>
            <CommonTemplate page={HealthcheckPage} />
          </Suspense>
        }
      />
      <Route
        path="/auth/google_oauth2"
        element={
          <Suspense fallback={fallback}>
            <CommonTemplate page={OAuthCallback} />
          </Suspense>
        }
      />

      {/* Protected */}
      <Route element={<ProtectedRouteWrapper />}>
        <Route
          path="/"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={Dashboard} />
            </Suspense>
          }
        />
        <Route
          path="/dashboard"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={Dashboard} />
            </Suspense>
          }
        />

        {/* Plans */}
        <Route
          path="/plans"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PlansList} />
            </Suspense>
          }
        />
        <Route
          path="/plans/new"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PlansForm} />
            </Suspense>
          }
        />
        <Route
          path="/plans/:id/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PlansForm} />
            </Suspense>
          }
        />
        <Route
          path="/plans/:id"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PlansView} />
            </Suspense>
          }
        />
        {/* Legacy redirects */}
        <Route path="/plans/create" element={<Navigate to="/plans/new" replace />} />
        <Route path="/plans/edit/:id" element={<EditRedirect base="/plans" />} />

        {/* Coupons */}
        <Route
          path="/coupons"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={CouponsList} />
            </Suspense>
          }
        />
        <Route
          path="/coupons/new"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={CouponsForm} />
            </Suspense>
          }
        />
        <Route
          path="/coupons/:id/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={CouponsForm} />
            </Suspense>
          }
        />
        <Route
          path="/coupons/:id"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={CouponsView} />
            </Suspense>
          }
        />
        <Route
          path="/coupons/:id/coupon-plans"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={CouponPlansList} />
            </Suspense>
          }
        />
        <Route
          path="/coupons/:id/coupon-plans/new"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={CouponPlansForm} />
            </Suspense>
          }
        />
        <Route
          path="/coupons/:id/coupon-plans/:idEdit/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={CouponPlansForm} />
            </Suspense>
          }
        />
        <Route
          path="/coupons/:id/coupon-plans/:idEdit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={CouponPlansView} />
            </Suspense>
          }
        />
        {/* Legacy redirects */}
        <Route path="/coupons/create" element={<Navigate to="/coupons/new" replace />} />
        <Route path="/coupons/edit/:id" element={<EditRedirect base="/coupons" />} />
        <Route
          path="/coupons/:id/coupon-plans/edit/:idEdit"
          element={<CouponPlanEditRedirect />}
        />

        {/* Promoitems */}
        <Route
          path="/promoitems"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PromoItemsList} />
            </Suspense>
          }
        />
        <Route
          path="/promoitems/new"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PromoItemsForm} />
            </Suspense>
          }
        />
        <Route
          path="/promoitems/:id/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PromoItemsForm} />
            </Suspense>
          }
        />
        <Route
          path="/promoitems/:id"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PromoItemsView} />
            </Suspense>
          }
        />
        <Route
          path="/promoitems/:id/promoitems-plans"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PromoitemsPlansList} />
            </Suspense>
          }
        />
        <Route path="/promoitems/:id/promoitems-plans/new" element={<PromoitemsPlanHubNewRedirect />} />
        <Route
          path="/promoitems/:id/promoitems-plans/:idEdit/edit"
          element={<PromoitemsPlanHubEditRedirect />}
        />
        <Route
          path="/promoitems/:id/promoitems-plans/:idEdit"
          element={<PromoitemsPlanHubDetailRedirect />}
        />
        <Route
          path="/promoitem-products"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PromoitemProducts} />
            </Suspense>
          }
        />
        <Route
          path="/promoitem-products/new"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PromoitemProductsForm} />
            </Suspense>
          }
        />
        <Route
          path="/promoitem-products/:id/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PromoitemProductsForm} />
            </Suspense>
          }
        />
        <Route
          path="/promoitem-products/:id"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PromoitemProductsView} />
            </Suspense>
          }
        />
        {/* Legacy redirects */}
        <Route path="/promoitems/edit/:id" element={<EditRedirect base="/promoitems" />} />
        <Route
          path="/promoitems/:id/promoitems-plans/edit/:idEdit"
          element={<PromoitemsPlanEditRedirect />}
        />
        <Route
          path="/promoitem-products/edit/:id"
          element={<EditRedirect base="/promoitem-products" />}
        />

        {/* Leituverso */}
        <Route
          path="/leituverso"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={Leituverso} />
            </Suspense>
          }
        />
        <Route
          path="/leituverso/new"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={LeituversoForm} />
            </Suspense>
          }
        />
        <Route
          path="/leituverso/:id/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={LeituversoForm} />
            </Suspense>
          }
        />
        <Route
          path="/leituverso/:id"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={LeituversoView} />
            </Suspense>
          }
        />
        {/* Legacy redirects */}
        <Route path="/leituverso/create" element={<Navigate to="/leituverso/new" replace />} />
        <Route path="/leituverso/edit/:id" element={<EditRedirect base="/leituverso" />} />

        {/* Promotions */}
        <Route
          path="/promotions"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PromotionsList} />
            </Suspense>
          }
        />
        <Route
          path="/promotions/new"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PromotionsForm} />
            </Suspense>
          }
        />
        <Route
          path="/promotions/:id/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PromotionsForm} />
            </Suspense>
          }
        />
        <Route
          path="/promotions/:id"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PromotionsView} />
            </Suspense>
          }
        />
        {/* Legacy redirect */}
        <Route path="/promotions/edit/:id" element={<EditRedirect base="/promotions" />} />

        {/* Ambassadors */}
        <Route
          path="/ambassadors"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={AmbassadorsList} />
            </Suspense>
          }
        />
        <Route
          path="/ambassadors/new"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={AmbassadorsForm} />
            </Suspense>
          }
        />
        <Route
          path="/ambassadors/:id/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={AmbassadorsForm} />
            </Suspense>
          }
        />
        <Route
          path="/ambassadors/:id"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={AmbassadorsView} />
            </Suspense>
          }
        />
        <Route
          path="/ambassadors/:ambassadorId/indications/new"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={IndicationsForm} />
            </Suspense>
          }
        />
        {/* Legacy redirects */}
        <Route path="/ambassadors/create" element={<Navigate to="/ambassadors/new" replace />} />
        <Route path="/ambassadors/edit/:id" element={<EditRedirect base="/ambassadors" />} />

        {/* Indications — edição standalone */}
        <Route
          path="/indications/:id/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={IndicationsForm} />
            </Suspense>
          }
        />
        {/* Legacy redirect */}
        <Route path="/indications/edit/:id" element={<EditRedirect base="/indications" />} />

        {/* Prizes */}
        <Route
          path="/prizes"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PrizesList} />
            </Suspense>
          }
        />
        <Route
          path="/prizes/new"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PrizesForm} />
            </Suspense>
          }
        />
        <Route
          path="/prizes/:id/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PrizesForm} />
            </Suspense>
          }
        />
        <Route
          path="/prizes/:id"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PrizesView} />
            </Suspense>
          }
        />
        {/* Legacy redirects */}
        <Route path="/prizes/create" element={<Navigate to="/prizes/new" replace />} />
        <Route path="/prizes/edit/:id" element={<EditRedirect base="/prizes" />} />

        {/* Levels */}
        <Route
          path="/levels"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={LevelsList} />
            </Suspense>
          }
        />
        <Route
          path="/levels/new"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={LevelsForm} />
            </Suspense>
          }
        />
        <Route
          path="/levels/:id/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={LevelsForm} />
            </Suspense>
          }
        />
        <Route
          path="/levels/:id"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={LevelsView} />
            </Suspense>
          }
        />
        {/* Legacy redirects */}
        <Route path="/levels/create" element={<Navigate to="/levels/new" replace />} />
        <Route path="/levels/edit/:id" element={<EditRedirect base="/levels" />} />

        {/* Campaigns — redirect legado */}
        <Route path="/campaigns/promoitems" element={<Navigate to="/promoitems" replace />} />
        <Route path="/campaigns/promoitems/*" element={<Navigate to="/promoitems" replace />} />

        {/* CMS Manager */}
        <Route
          path="/cms"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={CMSDashboard} />
            </Suspense>
          }
        />
        <Route
          path="/cms/sites"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={SitesManager} />
            </Suspense>
          }
        />
        <Route
          path="/cms/sites/new"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={SiteForm} />
            </Suspense>
          }
        />
        <Route
          path="/cms/sites/:configId/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={SiteForm} />
            </Suspense>
          }
        />
        <Route
          path="/cms/plans"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PlansManager} />
            </Suspense>
          }
        />
        <Route
          path="/cms/plans/new"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PlanForm} />
            </Suspense>
          }
        />
        <Route
          path="/cms/plans/:planIndex/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={PlanForm} />
            </Suspense>
          }
        />
        <Route
          path="/cms/leituverso"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={CmsLeituversosManager} />
            </Suspense>
          }
        />
        <Route
          path="/cms/leituverso/new"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={CmsLeituversoForm} />
            </Suspense>
          }
        />
        <Route
          path="/cms/leituverso/:modalIndex/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={CmsLeituversoForm} />
            </Suspense>
          }
        />
        <Route
          path="/cms/flexgift"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={FlexGiftForm} />
            </Suspense>
          }
        />
        <Route
          path="/cms/flexgift/edit"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={FlexGiftForm} />
            </Suspense>
          }
        />
        {/* CMS Legacy redirects */}
        <Route path="/cms/sites/create" element={<Navigate to="/cms/sites/new" replace />} />
        <Route path="/cms/sites/edit/:configId" element={<CmsSiteEditRedirect />} />
        <Route path="/cms/plans/create" element={<Navigate to="/cms/plans/new" replace />} />
        <Route path="/cms/plans/edit/:planIndex" element={<CmsPlanEditRedirect />} />
        <Route path="/cms/leituverso/create" element={<Navigate to="/cms/leituverso/new" replace />} />
        <Route path="/cms/leituverso/edit/:modalIndex" element={<CmsLeituversoEditRedirect />} />

        {/* Catch-all */}
        <Route
          path="*"
          element={
            <Suspense fallback={fallback}>
              <DashboardTemplate page={Dashboard} />
            </Suspense>
          }
        />
      </Route>
    </Routes>
  );
}
